import {
  createForm,
  Flex,
  Heading,
  Select,
  Spinner,
  Text,
  Textarea,
  TextInput,
  useToasts,
} from '@applyboard/crystal-ui';
import { useProgramDetails } from '../context/ProgramDetailsContext';
import styled from '@emotion/styled';
import { ErrorHandler } from '~/pages/ErrorPage';
import { useMemo } from 'react';
import { usePatchProgram } from '../data/usePatchProgram';
import { Program } from '../data/useProgram';
import { useFieldsOfStudy } from '../data/useFieldsOfStudy';
import {
  COMMITMENT_OPTIONS,
  DELIVERY_METHOD_OPTIONS,
  LANGUAGE_PROGRAM_OPTIONS,
  WORK_PRACTICUM_OPTIONS,
  LENGTH_UNIT_OPTIONS,
  TYPE_OF_PROGRAM_OPTIONS,
} from '../constants';
import { ProgramFormActions, hasFormChanges } from './ProgramFormActions';

type ProgramFormFields = {
  code: string;
  name: string;
  description: string;
  programLevelName: string;
  programType: string;
  fieldOfStudyId: string;
  programLength: string;
  lengthUnit: string;
  commitment: string;
  isLanguageProgram: string;
  url: string;
  deliveryMethod: string;
  workPracticum: string;
  applicationFee: string;
  tuition: string;
  tuitionPeriod: string;
  isPublished: string;
};

type ProgramInformationProps = {
  allowedToEdit: boolean;
  setAllowedToEdit: (allowedToEdit: boolean) => void;
};

export function ProgramInformation({ allowedToEdit, setAllowedToEdit }: ProgramInformationProps) {
  const toast = useToasts();
  const { program, isLoadingProgram, isProgramError } = useProgramDetails();
  const { patchProgram, isPatchingProgram } = usePatchProgram(program?.id || '');
  const { fieldsOfStudyData, isLoadingFieldsOfStudy } = useFieldsOfStudy();

  const { Form, Field, useFieldValues, useSetFieldValues } = createForm<ProgramFormFields>();

  const fieldsOfStudyOptions = useMemo(() => {
    return Object.entries(fieldsOfStudyData).map(([id, { name }]) => ({
      label: name,
      value: id,
    }));
  }, [fieldsOfStudyData]);

  const formDefaultValues: ProgramFormFields = useMemo(() => {
    if (!program) {
      return {
        code: '',
        name: '',
        description: '',
        programLevelName: '',
        programType: '',
        fieldOfStudyId: '',
        programLength: '',
        lengthUnit: '',
        isLanguageProgram: '',
        url: '',
        deliveryMethod: '',
        commitment: '',
        workPracticum: '',
        applicationFee: '',
        tuition: '',
        tuitionPeriod: '',
        isPublished: '',
      };
    }
    return {
      code: program.code || '',
      name: program.name || '',
      description: program.description || '',
      programLevelName: program.programLevelName || '',
      programType: program.typeOfProgram || '',
      fieldOfStudyId: program.fieldOfStudyId || '',
      programLength: program.length ? program.length.toString() : '',
      lengthUnit: program.lengthUnit || '',
      commitment: program.commitment || '',
      isLanguageProgram: program.isLanguageProgram === null ? '' : program.isLanguageProgram ? 'true' : 'false',
      url: program.url || '',
      deliveryMethod: program.deliveryMethod || '',
      workPracticum: program.workPracticum || '',
      applicationFee: program.applicationFee ? `$${program.applicationFee}` : '',
      tuition: program.tuition ? `$${program.tuition}` : '',
      tuitionPeriod: capitalize(program.tuitionPeriod) || '',
      isPublished: program.isPublished === null ? '' : program.isPublished ? 'Published' : 'Unpublished',
    };
  }, [program]);

  if (!isProgramError && isLoadingProgram) {
    return (
      <Flex p={5} gap={5} align="center" direction="column" role="progressbar">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isProgramError || !program) {
    return (
      <ErrorHandler
        error={isProgramError || new Error('Program not found')}
        customMessage="Failed to fetch the program details."
      />
    );
  }

  return (
    <Flex direction="column" gap={8}>
      <Heading contrast="high" level={2} variant="titleS">
        Program Information
      </Heading>
      <FormWrapper>
        <Form
          defaultValues={formDefaultValues}
          onSubmit={async (values) => {
            if (!hasFormChanges(values, formDefaultValues)) {
              return;
            }

            const validationErrors: string[] = [];
            if (!values.commitment || values.commitment.trim() === '') {
              validationErrors.push('Commitment is required');
            }
            if (!values.fieldOfStudyId || values.fieldOfStudyId.trim() === '') {
              validationErrors.push('Field of Study is required');
            }
            if (!values.lengthUnit || values.lengthUnit.trim() === '') {
              validationErrors.push('Length Unit is required');
            }
            if (!values.isLanguageProgram || values.isLanguageProgram.trim() === '') {
              validationErrors.push('Language Program is required');
            }
            if (!values.deliveryMethod || values.deliveryMethod.trim() === '') {
              validationErrors.push('Delivery Method is required');
            }

            if (validationErrors.length > 0) {
              toast.negative(new Error(`Please fill in all required fields:\n${validationErrors.join('\n')}`));
              return;
            }

            try {
              const attributes = transformFormValuesToApiFormat(values, program);
              await patchProgram(attributes as Parameters<typeof patchProgram>[0]);
              toast.positive('Your changes were successfully saved.');
              setAllowedToEdit(false);
            } catch (error) {
              toast.negative(new Error('Failed to save changes. Please try again.', { cause: error }));
            }
          }}
        >
          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="code" label="Program Code" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={TextInput}
                name="name"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Program Name <Asterisk />
                  </Text>
                }
                disabled={!allowedToEdit}
                validate={validateRequired}
              />
            </Flex.Item>
            <Flex.Item basis="100%">
              <Field
                as={Textarea}
                name="description"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Program Description <Asterisk />
                  </Text>
                }
                disabled={!allowedToEdit}
                validate={validateRequired}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field as={TextInput} name="programLevelName" label="Program Level" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={Select}
                name="programType"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Program Type
                  </Text>
                }
                appearance="styled"
                disabled={!allowedToEdit}
              >
                {TYPE_OF_PROGRAM_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={Select}
                name="fieldOfStudyId"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Field of Study
                  </Text>
                }
                appearance="styled"
                disabled={!allowedToEdit || isLoadingFieldsOfStudy}
                required="This field is required"
              >
                {fieldsOfStudyOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {allowedToEdit ? (
                <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
                  <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                    <Field
                      as={TextInput}
                      name="programLength"
                      label={
                        <Text variant="bodyS" contrast="mid">
                          Length <Asterisk />
                        </Text>
                      }
                      validate={validateRequiredNumber}
                    />
                  </Flex.Item>
                  <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                    <Field
                      as={Select}
                      name="lengthUnit"
                      label={
                        <Text variant="bodyS" contrast="mid">
                          Length Unit
                        </Text>
                      }
                      appearance="styled"
                      disabled={!allowedToEdit}
                      required="This field is required"
                    >
                      {LENGTH_UNIT_OPTIONS.map((option) => (
                        <Select.Option key={option.value} value={option.value} label={option.label} />
                      ))}
                    </Field>
                  </Flex.Item>
                </Flex>
              ) : (
                <Field as={TextInput} name="programLength" label={`Length (${program?.lengthUnit})`} disabled />
              )}
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={Select}
                name="commitment"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Commitment
                  </Text>
                }
                appearance="styled"
                disabled={!allowedToEdit}
                required="This field is required"
              >
                {COMMITMENT_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={Select}
                name="isLanguageProgram"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Language Program
                  </Text>
                }
                appearance="styled"
                disabled={!allowedToEdit}
                required="This field is required"
              >
                {LANGUAGE_PROGRAM_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis="100%">
              <Field
                as={TextInput}
                name="url"
                label={
                  <Text variant="bodyS" contrast="mid">
                    URL <Asterisk />
                  </Text>
                }
                disabled={!allowedToEdit}
                validate={validateRequired}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={Select}
                name="deliveryMethod"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Delivery Method
                  </Text>
                }
                appearance="styled"
                disabled={!allowedToEdit}
                required="This field is required"
              >
                {DELIVERY_METHOD_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={Select}
                name="workPracticum"
                label={
                  <Text variant="bodyS" contrast="mid">
                    Work Practicum
                  </Text>
                }
                appearance="styled"
                disabled={!allowedToEdit}
              >
                {WORK_PRACTICUM_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field as={TextInput} name="applicationFee" label="Application Fee" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field as={TextInput} name="tuition" label="Tuition" disabled />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field as={TextInput} name="tuitionPeriod" label="Tuition Period" disabled />
            </Flex.Item>
            <Flex.Item basis="100%">
              <Field as={TextInput} name="isPublished" label="Publication Status" disabled />
            </Flex.Item>
          </Flex>
          <ProgramFormActions
            allowedToEdit={allowedToEdit}
            isPatchingProgram={isPatchingProgram}
            useFieldValues={useFieldValues}
            useSetFieldValues={useSetFieldValues}
            formDefaultValues={formDefaultValues}
            setAllowedToEdit={setAllowedToEdit}
          />
        </Form>
      </FormWrapper>
    </Flex>
  );
}

function capitalize(value: string | undefined) {
  if (!value) return undefined;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function validateRequired(value: string | undefined): string | true {
  if (!value || value.trim() === '') {
    return 'This field is required';
  }
  return true;
}

function validateRequiredNumber(value: string | undefined): string | true {
  if (!value || value.trim() === '') {
    return 'This field is required';
  }
  const num = parseFloat(value);
  if (isNaN(num) || num < 0) {
    return 'Please enter a valid number';
  }
  return true;
}

function transformFormValuesToApiFormat(
  values: ProgramFormFields,
  originalProgram: Program | undefined
): Partial<{
  name: string;
  description: string;
  typeOfProgram: string | null;
  fieldOfStudyId: string;
  length: number;
  lengthUnit: string;
  commitment: string;
  isLanguageProgram: boolean;
  url: string;
  deliveryMethod: string;
  workPracticum: string | null;
}> {
  const attributes: Record<string, unknown> = {};

  if (!originalProgram) {
    return attributes;
  }

  if (values.name !== originalProgram.name) {
    attributes.name = values.name;
  }

  if (values.description !== originalProgram.description) {
    attributes.description = values.description;
  }

  const typeOfProgram =
    values.programType && values.programType.trim() !== ''
      ? values.programType.toLowerCase().replace(/\s+/g, '_')
      : null;
  const originalTypeOfProgram = originalProgram.typeOfProgram?.toLowerCase().replace(/\s+/g, '_') || null;
  if (typeOfProgram !== originalTypeOfProgram) {
    attributes.typeOfProgram = typeOfProgram;
  }

  if (values.fieldOfStudyId && values.fieldOfStudyId !== originalProgram.fieldOfStudyId) {
    attributes.fieldOfStudyId = values.fieldOfStudyId;
  }

  const length = values.programLength ? parseFloat(values.programLength) : undefined;
  if (length !== undefined && !isNaN(length) && length !== originalProgram.length) {
    attributes.length = length;
  }

  if (values.lengthUnit !== originalProgram.lengthUnit) {
    attributes.lengthUnit = values.lengthUnit;
  }

  const commitment = values.commitment ? values.commitment.toLowerCase().replace(/\s+/g, '_') : undefined;
  const originalCommitment = originalProgram.commitment?.toLowerCase();
  if (commitment !== originalCommitment) {
    attributes.commitment = commitment;
  }

  const isLanguageProgram = values.isLanguageProgram === 'true';
  if (isLanguageProgram !== originalProgram.isLanguageProgram) {
    attributes.isLanguageProgram = isLanguageProgram;
  }

  if (values.url !== originalProgram.url) {
    attributes.url = values.url;
  }

  if (values.deliveryMethod && values.deliveryMethod !== originalProgram.deliveryMethod) {
    attributes.deliveryMethod = values.deliveryMethod;
  }

  const workPracticum = values.workPracticum && values.workPracticum.trim() !== '' ? values.workPracticum : null;
  if (workPracticum !== (originalProgram.workPracticum || null)) {
    attributes.workPracticum = workPracticum;
  }

  return attributes;
}

export function Asterisk() {
  return (
    <Text as="span" aria-hidden intent="negative" variant="labelM" contrast="mid">
      *
    </Text>
  );
}

const FormWrapper = styled.div`
  width: 100%;
  max-width: 800px;
`;
